/**
 * Racks draft/name/clone bindings for the descriptor engine (#997 Story 6.3 R7).
 *
 * The engine's generic factories (`descriptorCreateDraft` /
 * `descriptorEnsureNameForCreate` / `descriptorCloneForCreate`) don't match the
 * bespoke Racks CREATE-flow semantics: bespoke drafts carry
 * `DEFAULT_RACK_CONFIGURATION` (Quantity pre-filled to 1), auto-names are serial
 * hashes (`Rack-102H33`, `generateAutoRackName`), and duplicates take a hash
 * name while dropping id/metadata/`rackPduAdditionalInfo`. Per the story ADR
 * (parity by reuse), each binding here delegates to the PROVEN bespoke util and
 * only converts shapes at the engine boundary (`RackConfiguration` ↔
 * `DescriptorInstance` via the 6.2 converters).
 */

import {
  cloneRackForCreate,
  createDraftRackConfiguration,
} from '../../../../components/racks/rackListUtils';
import {
  descriptorInstanceToRack,
  rackToDescriptorInstance,
} from '../../../../services/descriptorEditSessionFlush';
import { generateAutoRackName } from '../../../../utils/rackAutoName';
import { RACKS_DESCRIPTOR } from '../../../sections/racks.descriptor';

import type { UpdateRackConfigurationInput } from '../../../../types/rack';
import type {
  DescriptorInstance,
  DescriptorInstancePatch,
} from '../../../sections/descriptorEngineAdapter';

function patchToRackInput(
  data: DescriptorInstancePatch,
): UpdateRackConfigurationInput {
  const { instanceName, instanceStatus, ...fields } = data;
  return {
    ...fields,
    rackName: typeof instanceName === 'string' ? instanceName : undefined,
    rackStatus: instanceStatus as UpdateRackConfigurationInput['rackStatus'],
  } as UpdateRackConfigurationInput;
}

/** Bespoke draft factory: DEFAULT_RACK_CONFIGURATION + normalizeRackStatus. */
export function racksCreateDraft(
  data: DescriptorInstancePatch,
  displayOrder: number,
  tempId: number,
): DescriptorInstance {
  const rack = createDraftRackConfiguration(
    patchToRackInput(data),
    displayOrder,
    tempId,
  );
  return rackToDescriptorInstance(rack) as DescriptorInstance;
}

/** Bespoke auto-name: serial hash (`Rack-102H33`), mirrors ensureRackNameForCreate. */
export function racksEnsureNameForCreate(
  data: DescriptorInstancePatch,
  tempId: number,
  takenNames: Iterable<string | undefined>,
): DescriptorInstancePatch {
  if (typeof data.instanceName === 'string' && data.instanceName.trim()) {
    return data;
  }
  return { ...data, instanceName: generateAutoRackName(tempId, takenNames) };
}

/** Bespoke duplicate: hash name, keeps status, drops id/metadata/pdu-additional-info. */
export function racksCloneForCreate(
  source: DescriptorInstance,
  takenNames: Iterable<string | undefined>,
  tempId: number,
): Partial<DescriptorInstance> {
  const sourceRack = descriptorInstanceToRack(source, 0, RACKS_DESCRIPTOR);
  const cloned = cloneRackForCreate(sourceRack, takenNames, tempId);
  return rackToDescriptorInstance({
    ...cloned,
    id: tempId,
  } as Parameters<typeof rackToDescriptorInstance>[0]) as Partial<DescriptorInstance>;
}
